import { FC, useMemo, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { Button } from "components/buttons/Button"
import { ModalConfirmDestructive } from "components/modals/ModalConfirmDestructive"

import { settingsService } from "services/settings/settings.service"
import { SettingsResetTarget } from "services/settings/type"

import { ResetToDefaultButton } from "./ResetToDefaultButton"
import { SectionCard } from "./SectionCard"
import { useResetTargets } from "./useResetTargets"

interface DangerZoneTabProps {
  /** Reloads the page's settings after a reset so the other tabs stop showing stale values. */
  onRefresh: () => Promise<void>
  /** Translated tab labels, so a group is named here exactly as its tab is named. */
  tabLabels: Record<string, string>
}

/**
 * The single home for destructive settings actions.
 *
 * Every other tab carries only the reset for the group it owns; the platform-wide reset lives
 * here, behind a typed confirmation and an explicit list of what it touches.
 */
export const DangerZoneTab: FC<DangerZoneTabProps> = ({ onRefresh, tabLabels }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const { targets, resetAllConfirmation, loading, describe, refresh } = useResetTargets()
  const [resetAllOpen, setResetAllOpen] = useState(false)
  const [resettingAll, setResettingAll] = useState(false)

  const labelFor = (target: SettingsResetTarget) => tabLabels[target.tab] ?? target.tab

  /** Grouped by owning tab: an operator thinks in tabs, not in storage columns. */
  const byTab = useMemo(() => {
    return targets.reduce<Record<string, SettingsResetTarget[]>>((acc, target) => {
      acc[target.tab] = [...(acc[target.tab] ?? []), target]
      return acc
    }, {})
  }, [targets])

  const resetAllConsequences = useMemo(
    () =>
      targets
        .filter((target) => target.includeInResetAll)
        .map((target) => `${labelFor(target)} — ${describe(target.id, labelFor(target))}`),
    [targets, tabLabels, describe]
  )

  const handleResetAll = async () => {
    setResettingAll(true)
    try {
      const outcome = await settingsService.resetAllSettings(resetAllConfirmation)

      if (!outcome) {
        toast.error(t("settings.danger.resetAllFailed") as string)
        return
      }

      if (outcome.failed.length) {
        // A partial reset is reported per group rather than as one vague failure.
        outcome.failed.forEach(({ id }) => toast.error(t("settings.danger.resetAllPartial", { section: id }) as string))
      }

      toast.success(t("settings.danger.resetAllSuccess", { count: outcome.reset.length }) as string)
      setResetAllOpen(false)
      await refresh()
      await onRefresh()
    } finally {
      setResettingAll(false)
    }
  }

  if (loading) {
    return <p className="mt-6 text-sm text-gray-500 dark:text-gray-400">{t("common.loading")}</p>
  }

  return (
    <section className="flex flex-col gap-6 mt-6">
      <div className="rounded-lg border border-error/40 bg-error/5 p-4">
        <h3 className="text-xl font-semibold text-error">{t("settings.danger.title")}</h3>
        <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">{t("settings.danger.intro")}</p>
      </div>

      <SectionCard title={t("settings.danger.perSectionTitle")}>
        <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">{t("settings.danger.perSectionIntro")}</p>

        <div className="flex flex-col gap-3">
          {Object.entries(byTab).map(([tab, tabTargets]) => (
            <div key={tab} className="rounded-lg border border-black/10 dark:border-white/10 p-3">
              <p className="mb-2 text-sm font-semibold text-gray-800 dark:text-white">{tabLabels[tab] ?? tab}</p>

              {tabTargets.map((target) => (
                <div key={target.id} className="flex flex-wrap items-center justify-between gap-3 py-2">
                  <p className="max-w-3xl text-sm text-gray-600 dark:text-gray-400">{describe(target.id, labelFor(target))}</p>
                  <ResetToDefaultButton
                    target={target.id}
                    label={labelFor(target)}
                    onDone={onRefresh}
                    className="btn-outline btn-sm"
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard title={t("settings.danger.resetAllTitle")}>
        <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">{t("settings.danger.resetAllIntro")}</p>

        <Button variant="error" onClick={() => setResetAllOpen(true)}>
          {t("settings.danger.resetAllButton")}
        </Button>
      </SectionCard>

      <ModalConfirmDestructive
        open={resetAllOpen}
        onClose={() => setResetAllOpen(false)}
        onConfirm={handleResetAll}
        loading={resettingAll}
        title={t("settings.danger.resetAllTitle")}
        description={t("settings.danger.resetAllConfirm")}
        consequences={resetAllConsequences}
        confirmLabel={t("settings.danger.resetAllButton")}
        confirmPhrase={resetAllConfirmation}
      />
    </section>
  )
}
